import React from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Typography,
  styled,
} from '@mui/material';

import { ErrorContent } from '../error/ErrorContent';
import {
  DetailedCFRangeGraphLegend,
  DetailedNonLiveCFRangeGraphLegend,
} from '../graphs/DetailedCFRangeGraph';
import { RangeGraphContainer } from '../graphs/RangeGraphContainer';
import { DoubleLabelSwitch } from '../inputs/switches/DoubleLabelSwitch';
import {
  useLatestRiskToggle,
  type LatestRiskToggle,
} from '../hooks/useLatestRiskToggle';
import { useAccountRiskDetails } from '../hooks/useAccountRiskDetails';
import { useNormalizeCowbellFactorRiskDetails } from './useNormalizeCowbellFactorRiskDetails';
import { CowbellFactor, CowbellFactorGraph } from './CowbellFactor';

interface PolicyCowbellFactorsPanelProps {
  readonly policy: any;
  readonly accountId?: string;
  /** Controlled toggle (e.g. the drawer shares one across tabs); else self-owned. */
  readonly latestRisk?: LatestRiskToggle;
  /** Account surface: force Latest Risk and hide the switch entirely. */
  readonly hideToggle?: boolean;
  /**
   * Card background. Defaults to 'paper' to match the sibling cards in the
   * table accordions; the policy-details drawer passes 'default' so it matches
   * its gray sibling tabs.
   */
  readonly cardBackground?: 'paper' | 'default';
}

/**
 * The single shared Cowbell Factors panel for every Policy surface: a
 * locked/live toggle, the matching legend, the aggregate graph, and a range
 * graph for every factor. Company values are locked to the policy on "Last
 * Underwriting" and live on "Latest Risk"; ranges always come from live risk.
 */
export function PolicyCowbellFactorsPanel({
  policy,
  accountId,
  latestRisk,
  hideToggle,
  cardBackground = 'paper',
}: Readonly<PolicyCowbellFactorsPanelProps>) {
  const ownToggle = useLatestRiskToggle();
  const resolved = latestRisk ?? ownToggle;
  const latestRiskSelected = hideToggle ? true : resolved.latestRiskSelected;
  const handleLatestRiskToggle = resolved.handleLatestRiskToggle;

  const {
    data: accountRiskDetails,
    isLoading,
    isError,
  } = useAccountRiskDetails({
    accountId: accountId ?? policy?.accountId,
  });

  const factors = useNormalizeCowbellFactorRiskDetails({
    accountRiskDetails: accountRiskDetails ?? { details: {} },
    latestRiskSelected,
    policy,
  });

  if (isLoading) {
    return (
      <PanelCard cardBg={cardBackground}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size="3rem" />
        </Box>
      </PanelCard>
    );
  }

  // The risk fetch feeds the whole panel, so a failure has nothing to render.
  // Surface it in place rather than leaving an empty card behind the toggle.
  if (isError) {
    return (
      <PanelCard cardBg={cardBackground}>
        <Box textAlign="center" py={2}>
          <ErrorContent label="Uh oh! It appears a system error has occurred and Cowbell Factors can not be retrieved at this time." />
        </Box>
      </PanelCard>
    );
  }

  const Legend = latestRiskSelected
    ? DetailedCFRangeGraphLegend
    : DetailedNonLiveCFRangeGraphLegend;
  const aggregate = getAggregateFactor(
    accountRiskDetails,
    policy,
    latestRiskSelected
  );

  if (!aggregate && factors.length === 0) {
    return (
      <PanelCard cardBg={cardBackground}>
        <Box textAlign="center" py={2}>
          <Typography variant="body2">
            No Cowbell Factors are available for this account.
          </Typography>
        </Box>
      </PanelCard>
    );
  }

  return (
    <PanelCard cardBg={cardBackground}>
      <Box display="flex" flexDirection="column">
        {!hideToggle && (
          <Box className="justify-center">
            <DoubleLabelSwitch
              onChange={handleLatestRiskToggle}
              value={latestRiskSelected}
              offLabel="Risk as of Last Underwriting"
              onLabel="Latest Risk"
            />
          </Box>
        )}
        <Legend
          legendContainerProps={{
            display: 'flex',
            justifyContent: 'center',
            style: { gap: '1rem' },
          }}
        />
      </Box>
      <Divider style={{ width: '100%', margin: '1rem 0' }} />

      {aggregate ? (
        <Box mb={2}>
          <RangeGraphContainer>
            <CowbellFactorGraph
              factor={aggregate}
              latestRiskSelected={latestRiskSelected}
            />
          </RangeGraphContainer>
          <Typography
            variant="body2"
            style={{ textAlign: 'center', marginTop: '0.5rem' }}
          >
            Aggregate CCF
          </Typography>
        </Box>
      ) : null}

      {factors.map((factor: any) => (
        <CowbellFactor
          key={factor.factorKey}
          factor={factor}
          latestRiskSelected={latestRiskSelected}
        />
      ))}
    </PanelCard>
  );
}

// Aggregate row: ranges are always live; company is the policy's locked
// `companyAggregateCF` on "Last Underwriting", else live.
const getAggregateFactor = (
  accountRiskDetails: any,
  policy: any,
  latestRiskSelected: boolean
) => {
  const aggregate = accountRiskDetails?.details?.companyAggregate;
  if (!aggregate) {
    return null;
  }
  const locked = policy?.cowbellRiskFactors?.companyAggregateCF;
  const useLocked = !latestRiskSelected && typeof locked === 'number';
  return { ...aggregate, company: useLocked ? locked : aggregate.company };
};

const PanelCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'cardBg',
})<{ readonly cardBg?: 'paper' | 'default' }>(({ theme, cardBg }: any) => ({
  padding: '1rem',
  backgroundColor:
    cardBg === 'default'
      ? theme.palette.background.default
      : theme.palette.background.paper,
}));

export default PolicyCowbellFactorsPanel;
